<template>
  <div
    ref="predictiveSearch"
    class="predictive-search container grid"
  >
    <div
      class="
        predictive-search__panel
        predictive-search__products-panel
        col xs12 l16
      "
    >
      <p
        v-if="!isMobile || isMobile && query"
        class="predictive-search__title text-heading-m-bold text-heading-s-bold-desktop"
        v-text="$string('search.products')"
      />

      <transition name="fade">
        <p
          v-if="query && noProducts"
          class="text-body-s text-body-s-desktop predictive-search__no-results container"
          v-html="$string('search.no_results_html', { replace: { query } })"
        />

        <p
          v-else-if="!query && !products.length"
          class="predictive-search__subtitle text-body-s"
          v-text="$string('search.start_typing')"
        />

        <div
          v-else
          ref="products"
          class="predictive-search__products-container"
        >
          <div class="predictive-search__products">
            <product-card
              v-for="(product, index) of products"
              :key="`product-${product.id}-${index}`"
              block-class="predictive-search"
              delay-click
              :index="index"
              :product="product"
              :show-wishlist-button="false"
              text-color
              is-search-instant
              tiny
              @view-product="(url) => handleProductClick(product.id, url)"
            />
          </div>
        </div>
      </transition>
    </div>

    <div
      class="
        predictive-search__panel
        predictive-search__suggestions-panel
        col xs12 l18-23
      "
    >
      <!-- Suggestions & Popular Searches -->
      <div
        v-if="!query || isSuggestions"
        class="predictive-search__suggestion-list"
      >
        <p
          class="predictive-search__title text-heading-m-bold text-heading-s-bold-desktop"
          v-text="suggestionsTitle"
        />

        <ul class="predictive-search__suggestions">
          <li
            v-for="(suggestion, index) of suggestions"
            :key="`${suggestion.handle}-${index}`"
            class="predictive-search__suggestion-item"
          >
            <component
              :is="isSuggestions ? 'button' : 'a'"
              class="
                predictive-search__suggestion
                text-utility-capitalized-m
                text-utility-capitalized-s-desktop
              "
              :href="isSuggestions ? null : suggestion.url"
              @click="handleSuggestionClick(suggestion.query)"
              v-html="suggestion.title"
            />
          </li>
        </ul>
      </div>

      <!-- Helpful Links -->
      <transition name="fade">
        <div
          v-if="!query && !products.length"
          class="predictive-search__helpful"
        >
          <div class="predictive-search__divider" />

          <p
            class="predictive-search__title text-heading-m-bold text-heading-s-bold-desktop"
            v-text="$string('search.helpful_links')"
          />

          <ul class="predictive-search__suggestions">
            <li
              v-for="link of helpfulLinks"
              :key="link.handle"
              class="predictive-search__suggestion-item"
            >
              <a
                class="
                  predictive-search__suggestion
                  text-utility-capitalized-m
                  text-body-s-desktop
                "
                :href="link.handle"
                v-html="link.title"
              />
            </li>
          </ul>
        </div>
      </transition>

      <!-- Collections -->
      <transition name="fade">
        <div
          v-if="response.collections?.length"
          class="predictive-search__collections"
          :class="{ 'no-margin': query && !isSuggestions }"
        >
          <div
            v-if="query && isSuggestions"
            class="predictive-search__divider"
          />

          <p
            class="predictive-search__title text-heading-m-bold text-heading-s-bold-desktop"
            v-text="$string('search.collections')"
          />

          <ul class="predictive-search__suggestions">
            <li
              v-for="(collection) of collections"
              :key="collection.id"
              :collection="collection.id"
              class="predictive-search__suggestion-item"
            >
              <a
                class="
                  predictive-search__suggestion
                  text-utility-capitalized-m
                  text-utility-capitalized-s-desktop
                "
                :href="'/collections/' + collection.handle"
                v-html="collection.title"
              />
            </li>
          </ul>
        </div>
      </transition>

      <!-- Pages -->
      <transition name="fade">
        <div
          v-if="response.pages?.length"
          class="predictive-search__pages"
          :class="{'no-margin': query && !isSuggestions && !response.collections?.length}"
        >
          <div
            v-if="response.collections?.length"
            class="predictive-search__divider"
          />

          <p
            class="predictive-search__title text-heading-m-bold text-heading-s-bold-desktop"
            v-text="$string('search.pages')"
          />

          <ul class="predictive-search__suggestions">
            <li
              v-for="(page) of pages"
              :key="page.id"
              :page="page.id"
              class="predictive-search__suggestion-item"
            >
              <a
                class="
                  predictive-search__suggestion
                  text-utility-capitalized-m
                  text-utility-capitalized-s-desktop
                "
                :href="page.url"
                v-html="page.title"
              />
            </li>
          </ul>
        </div>
      </transition>
    </div>

    <transition name="fade">
      <div
        v-if="response.products?.length"
        class="predictive-search__view-all col xs12 l24"
      >
        <btn
          :label="viewAllLabel"
          :show-icon="false"
          :url="viewAllUrl"
          modifiers="block"
        />
      </div>
    </transition>

    <button
      class="
        predictive-search__overlay-close
        icon-button
      "
      type="button"
      @click.prevent="handleCloseOverlay"
    >
      <span
        class="visually-hidden"
        v-text="$string('accessibility.close')"
      />

      <icon-close />
    </button>

    <boost-api-call
      endpoint="search/suggest"
      :limit="4"
      :query="query"
      :shop="$variable('store.localisedPermanentDomain')"
      @boost-prices-updated="boostPricesUpdated"
      @boost-updated="boostUpdated"
    />

    <boost-analytics
      :analytics-event="analyticsEvent"
      type="search"
      @analytics-submitted="handleAnalyticsSubmitted"
    />
  </div>
</template>

<script>

/**
 * Vue: Predictive search (predictive-search)
 * -----------------------------------------------------------------------------
 * Predictive search results.
 *
 * @param {Boolean} isActive - Search is active.
 * @param {String} query - Current search query.
 * @param {Array} searchBars - Search bar component references.
 * @param {Object} searchSettings - Search settings.
 * @param {Object} helpfulLinks - Predictive search helpful links settings.
 * @param {Object} popularSearches - Predictive search popular searches.
 *
 */
import { nextTick } from 'vue'
import { mapActions, mapState } from 'vuex'

import BoostAnalytics from '~async/boost-analytics/boost-analytics'
import BoostApiCall from '~global/boost-api-call/boost-api-call'
import Btn from '~global/btn/btn'
import ProductCard from '~async/product-card/product-card'
import IconClose from '~icons/misc/close.svg'

import { convertToHandle } from '~/helpers/convert'
import { formatProduct } from '~/helpers/format'
import { formatBoostProduct } from '~/helpers/format-boost'
import productQuery from '~/graphql/queries/product.gql'

export default {
  name: 'PredictiveSearch',

  components: {
    BoostAnalytics,
    BoostApiCall,
    Btn,
    ProductCard,
    IconClose,
  },

  props: {
    query: {
      type: String,
      default: '',
    },
    searchBars: {
      type: Array,
      default: () => [],
    },
    searchSettings: {
      type: Object,
      default: () => ({
        trendingProducts: [],
        collections: [],
        pages: [],
      }),
    },
    helpfulLinks: {
      type: Object,
      default: () => ({
        handle: '',
        title: '',
        url: '',
      }),
    },
    popularSearches: {
      type: Object,
      default: () => ({
        handle: '',
        title: '',
        url: '',
      }),
    },
  },

  emits: {
    'update-query': (payload) => typeof payload === 'string',
    'update-redirect': (payload) => typeof payload === 'string',
  },

  data() {
    return {
      analyticsEvent: {},
      boostSuggestions: [],
      noProducts: false,
      products: [false, false, false, false],
      redirect: '',
      response: {},
      storageKey: 'predictive-search',
      suggestions: [],
      trendingProducts: [],
      collections: [],
      pages: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute if suggestions are being shown.
     * @returns {Boolean}
     */
    isSuggestions() {
      return this.response.suggestions?.length
    },

    /**
     * Compute suggestions title.
     * @returns {String}
     */
    suggestionsTitle() {
      return this.isSuggestions
        ? this.$string('search.suggestions')
        : this.$string('search.popular_searches')
    },

    /**
     * Compute total results.
     * @returns {Number}
     */
    totalResults() {
      let totalResults = 0

      if (this.response.total_product) {
        totalResults += this.response.total_product
      }

      if (this.response.collections) {
        totalResults += this.response.collections.length
      }

      if (this.response.pages) {
        totalResults += this.response.pages.length
      }

      return totalResults
    },

    /**
     * Compute view all link URL.
     * @returns {String}
     */
    viewAllUrl() {
      return `${this.$variable('routes.search')}?q=${this.query}`
    },

    /**
     * Compute view all label.
     * @returns {String}
     */
    viewAllLabel() {
      return this.$string('search.view_all', {
        pluralise: this.totalResults,
        replace: { count: this.totalResults },
      })
    },
  },

  watch: {

    /**
     * Watch isActive to trap focus.
     */
    isActive() {
      this.updateFocusTrap()
    },

    /**
     * Watch query to reset to suggestions.
     */
    query() {
      if (this.query) {
        this.noProducts = false
        return
      }

      this.response = {}
    },

    /**
     * Watch products update to scroll to start of container.
     */
    products() {
      nextTick().then(() => {
        if (!this.$refs.products) {
          return
        }

        this.$refs.products.scrollTo(0, 0)
      })
    },

    /**
     * Watch redirect update to emit.
     */
    redirect() {
      this.$emit('update-redirect', this.redirect)
    },

    /**
     * Watch response data to update products, suggestions, and redirect.
     */
    response() {
      const regex = new RegExp(`(?<query>${this.query.toLowerCase().trim()})`, 'g')

      /**
       * Update products.
       */
      if (this.response.products?.length) {
        this.noProducts = false

        this.products = this.response.products
          .map((product) => {
            if (!product) {
              return false
            }

            return formatBoostProduct(product)
          })
          .filter(Boolean)
          .slice(0, 4)

      } else {
        this.noProducts = true
        this.products = this.trendingProducts
      }

      /**
       * Update suggestions.
       */
      if (this.response.suggestions?.length) {

        this.boostSuggestions = this.response.suggestions.map((suggestion) => {
          const title = suggestion.replace(regex, (_, $1) => {
            return `<span>${$1}</span>`
          })

          return {
            handle: convertToHandle(suggestion),
            query: suggestion,
            title,
            url: `/products/${suggestion.replace(' ', '-')}`,
          }
        })

        this.suggestions = this.boostSuggestions

      } else {
        this.suggestions = this.popularSearches
      }

      /**
       * Update collections.
       */
      if (this.response.collections?.length) {
        this.collections = this.response.collections
          .map((collection) => {
            if (!collection) {
              return false
            }

            const title = collection.title.toLowerCase().replace(this.query, `<span>${this.query.toLowerCase()}</span>`)

            return {
              handle: collection.handle,
              id: collection.id,
              title,
            }
          })
          .filter(Boolean)
          .slice(0, 4)

      } else {
        this.noCollections = true
      }

      /**
       * Update collections.
       */
      if (this.response.pages?.length) {
        this.pages = this.response.pages
          .map((page) => {
            if (!page) {
              return false
            }

            const title = page.title.toLowerCase().replace(this.query, `<span>${this.query.toLowerCase()}</span>`)

            return {
              url: page.url,
              id: page.id,
              title,
            }
          })
          .filter(Boolean)
          .slice(0, 4)

      } else {
        this.noPages = true
      }

      /**
       * Update other.
       */
      this.redirect = this.response.redirect || ''
      this.updateFocusTrap()
    },
  },

  mounted() {
    this.updateFocusTrap()
    this.queryTrendingProducts()
    this.suggestions = this.popularSearches
    this.collections = this.searchSettings.collections
    this.collections = this.searchSettings.pages
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      clearFocusTrap: 'accessibility/clearFocusTrap',
      setFocusTrap: 'accessibility/setFocusTrap',
    }),

    /**
     * Query trending products.
     */
    async queryTrendingProducts() {
      if (!this.searchSettings.trendingProducts.length) {
        this.products = []
        return
      }

      if (sessionStorage.getItem(this.storageKey)) {
        this.trendingProducts = JSON.parse(sessionStorage.getItem(this.storageKey))
        this.products = this.trendingProducts
        return
      }

      try {
        this.state = 'loading'

        const queue = this.searchSettings.trendingProducts.map((handle) => {
          return cnvs.Query({
            query: productQuery,
            variables: {
              country: cnvs.store.country,
              handle,
              language: cnvs.store.language,
            },
          })
        })

        const response = await Promise.allSettled(queue)

        this.trendingProducts = response.map((promise) => {
          if (promise.status !== 'fulfilled') {
            return false
          }

          return formatProduct(promise.value)
        }).filter(Boolean)

        sessionStorage.setItem(this.storageKey, JSON.stringify(this.trendingProducts))
        this.products = this.trendingProducts
        this.updateFocusTrap()
        this.state = 'ready'

      } catch (error) {
        this.products = []
        reportError('Failed to load trending products', error)
      }
    },

    /**
     * Callback for Boost API call.
     * @param {Object} response - Response from API call.
     */
    boostUpdated(response) {
      if (!response || !this.query) {
        return
      }

      this.response = response.data
    },

    /**
     * Callback from Shopify Markets price data is ready.
     * - Update existing products data with new prices by matching ID.
     * - If no localised product or prices then reveal price.
     * - Better to show an incorrect price than a loading state.
     * @param {Array} prices - Localised price data.
     */
    boostPricesUpdated(prices) {
      if (!prices.length) {
        this.products = this.products.map((product) => {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        })

        return
      }

      this.products = this.products.map((product) => {
        const localisedProduct = prices.find((localProduct) => {
          return localProduct.id === product.id
        })

        if (!localisedProduct) {
          return {
            ...product,
            price: {
              ...product.price,
              loading: false,
            },
          }
        }

        return {
          ...product,
          price: localisedProduct.price,
        }
      })
    },

    /**
     * Update focus trap.
     */
    async updateFocusTrap() {
      await new Promise((resolve) => {
        nextTick().then(resolve)
      })

      if (!this.isActive || !this.query) {
        this.clearFocusTrap()
        return
      }

      this.setFocusTrap({
        container: [
          ...this.searchBars,
          this.$refs.predictiveSearch,
        ],
      })
    },

    /**
     * Handle clicking a suggestion.
     * - Emits event to update search bar input.
     * @param {String} query - Suggestion query to emit.
     */
    handleSuggestionClick(query) {
      this.$emit('update-query', query)
    },

    /**
     * Handle product click to submit analytics event.
     * @param {Number} id - Product ID.
     * @param {String} url - Product URL.
     */
    handleProductClick(id, url) {
      this.analyticsEvent = {
        pid: String(id),
        u: 'view_product',
        url,
      }
    },

    /**
     * Handle close overlay click.
     */
    handleCloseOverlay() {
      this.closeOverlay(this.namespace)
    },

    /**
     * Handle analytics submitted event.
     * - Means submission is complete and we can continue with URL navigation.
     * @param {String} url - URL to navigate to.
     */
    handleAnalyticsSubmitted(url) {
      location.href = url
    },
  },
}
</script>

<style lang="scss">
@import './predictive-search';
</style>
